/**
 * Scrolls an element from it's current position to the desired position over a given period of time.
 * (Regardless of whether we want to scroll up or down)
 * Returns a promise once the scroll has ended.
 * @param element - the element to scroll.
 * @param to - the position to scroll the element to.
 * @param durationInSeconds - the duration over which to make the scroll.
 * @returns {Promise}
 */
export default (
  element,
  to = element.scrollHeight - element.clientHeight,
  durationInSeconds = 0
) => {
  return new Promise(resolve => {
    // Set scroll top without animation for duration 0
    if (!durationInSeconds) {
      element.scrollTop = to;
      resolve();
      return;
    }

    to = Math.min(Math.max(0, to), element.scrollHeight - element.clientHeight);

    const difference = to - element.scrollTop;
    // Complete the scrolling in 60 * durationInSeconds steps since the number of callbacks for requestAnimationFrame is usually 60 times per second
    const perTick = difference / (60 * durationInSeconds);

    const step = () => {
      const previousScroll = element.scrollTop;
      const diffLeft = to - element.scrollTop;
      element.scrollTop += diffLeft > 0 ? Math.min(diffLeft, perTick) : Math.max(diffLeft, perTick);

      // ScrollTop property does not accept fractions, so if we have less than 1 left (let's say 0.23), we will not try
      // to change the scrollTop since it's useless. That leaves us with a possible deviation of less than 1 pixel.
      if (Math.abs(to - element.scrollTop) >= 1 && previousScroll !== element.scrollTop) {
        requestAnimationFrame(step);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(step);
  });
};
